import {
  Anchor,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { formatHumanName } from '@medplum/core';
import { formatPractitionerStaffLabel } from '@unify/fhir-model/resources/practitioner';
import { displayLabelForEpisodeType } from '@/lib/fhirValueSets/sessionAndEpisodeTypes';
import type { EpisodeOfCare, Patient, Practitioner, Task, TaskOutput } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { UnifyTable, type UnifyTableColumn } from '../../../components/UnifyTable';
import { TASK_OUTPUT_SYSTEM } from '@unify/fhir-model/constants';
import { getOutputAssignmentDisposition } from '../../../fhir-model/resources/referral-task';
import { formatUsDateTime } from '@/lib/dateHelpers';
import { CaseloadTabPanelLayout } from '../components/CaseloadTabPanelLayout';
import { useSupervisionScope } from '../../supervision/hooks/useSupervisionScope';
import { useUrlFilterState } from '@/hooks/useUrlFilterState';
import { ReferralRequestCreateModal } from '../../referral-requests/ReferralRequestCreateModal';
import {
  type ReferralTaskRow,
  taskPreferencesFromRow,
  useReferralTasks,
} from './useReferralTasks';

const CURRENT_PROVIDER_ID = 'self';

function practitionerDisplayName(practitioner: Practitioner): string {
  return formatPractitionerStaffLabel(practitioner) || practitioner.id || 'Provider';
}

interface EpisodeInfo {
  patientName: string;
  serviceType: string;
  patientId: string;
}

function resolveSuperviseePractitionerId(providerFilter: string[]): string | null {
  const selected = providerFilter.filter((value) => value !== CURRENT_PROVIDER_ID);
  if (providerFilter.includes(CURRENT_PROVIDER_ID) && selected.length === 0) {
    return null;
  }
  return selected[0] ?? null;
}

function normalizeProviderFilter(next: string[], previous: string[]): string[] {
  if (next.length === 0) {
    return [CURRENT_PROVIDER_ID];
  }
  if (next.length === 1) {
    return next;
  }
  const newlyAdded = next.find((value) => !previous.includes(value));
  return [newlyAdded ?? next[next.length - 1] ?? CURRENT_PROVIDER_ID];
}

function getStatusColor(status: ReferralTaskRow['status']): string {
  const colorMap: Record<ReferralTaskRow['status'], string> = {
    Pending: 'gray',
    'In Progress': 'blue',
    Completed: 'green',
    Cancelled: 'red',
  };
  return colorMap[status] ?? 'gray';
}

function getFillPercentage(sent: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((sent / total) * 100);
}

function isAssignedEpisodeOutput(o: TaskOutput): boolean {
  return Boolean(
    o.type?.coding?.some((c) => c.system === TASK_OUTPUT_SYSTEM && c.code === 'assigned-episode'),
  );
}

interface ReferralsTabProps {
  /** Bumps when the caseload page or tab is revisited — triggers a fresh Medplum fetch. */
  refreshKey?: number;
}

export function ReferralsTab({ refreshKey }: ReferralsTabProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const navigate = useNavigate();
  const { role, superviseePractitionerIds } = useSupervisionScope();
  const isSupervisor = role === 'supervisor';

  const [providerFilter, setProviderFilter] = useState<string[]>([CURRENT_PROVIDER_ID]);
  const superviseePractitionerId = resolveSuperviseePractitionerId(providerFilter);
  const [superviseeFilterOptions, setSuperviseeFilterOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  useEffect(() => {
    if (!isSupervisor || superviseePractitionerIds.length === 0) {
      setSuperviseeFilterOptions([]);
      setProviderFilter([CURRENT_PROVIDER_ID]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const practitioners = await medplum.searchResources('Practitioner', {
          _id: superviseePractitionerIds.join(','),
          _count: String(superviseePractitionerIds.length),
        });
        if (cancelled) return;
        const byId = new Map(
          practitioners.filter((p): p is Practitioner & { id: string } => Boolean(p.id)).map((p) => [p.id, p]),
        );
        setSuperviseeFilterOptions(
          superviseePractitionerIds.map((id) => ({
            value: id,
            label: byId.has(id) ? practitionerDisplayName(byId.get(id)!) : id,
          })),
        );
      } catch {
        if (!cancelled) setSuperviseeFilterOptions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, isSupervisor, superviseePractitionerIds]);

  useEffect(() => {
    const selectedSuperviseeId = resolveSuperviseePractitionerId(providerFilter);
    if (selectedSuperviseeId && !superviseePractitionerIds.includes(selectedSuperviseeId)) {
      setProviderFilter([CURRENT_PROVIDER_ID]);
    }
  }, [providerFilter, superviseePractitionerIds]);

  const providerFilterOptions = useMemo(
    () => [
      { value: CURRENT_PROVIDER_ID, label: 'My Referrals' },
      ...superviseeFilterOptions,
    ],
    [superviseeFilterOptions],
  );

  const showSuperviseeProviderFilter = isSupervisor && superviseeFilterOptions.length > 0;

  const { rows, loading, error, refetch, createReferralRequest, cancelReferralRequest } = useReferralTasks({
    superviseePractitionerId,
  });

  useEffect(() => {
    if (refreshKey != null) {
      void refetch();
    }
  }, [refreshKey, refetch]);

  const [statusFilter, setStatusFilter] = useUrlFilterState('status', [
    'Pending',
    'In Progress',
    'Completed',
    'Cancelled',
  ]);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [viewedRequest, setViewedRequest] = useState<ReferralTaskRow | null>(null);

  const lockedPractitionerId =
    profile?.resourceType === 'Practitioner' ? profile.id : undefined;
  const providersById = useMemo((): Record<string, Practitioner | undefined> => {
    if (profile?.resourceType !== 'Practitioner' || !profile.id) return {};
    return { [profile.id]: profile };
  }, [profile]);

  const [episodeInfoMap, setEpisodeInfoMap] = useState<Record<string, EpisodeInfo>>({});
  const [episodesLoading, setEpisodesLoading] = useState(false);

  useEffect(() => {
    if (!viewedRequest) {
      setEpisodeInfoMap({});
      return;
    }
    const outputs = viewedRequest.sourceTask?.output?.filter(isAssignedEpisodeOutput) ?? [];
    const episodeIds = outputs
      .map((o) => o.valueReference?.reference?.replace('EpisodeOfCare/', ''))
      .filter((id): id is string => Boolean(id));
    if (episodeIds.length === 0) {
      setEpisodeInfoMap({});
      return;
    }
    setEpisodesLoading(true);
    void (async () => {
      try {
        const episodeResults = await Promise.all(
          episodeIds.map((id) => medplum.readResource('EpisodeOfCare', id).catch(() => null)),
        );
        const episodes = episodeResults.filter((e): e is NonNullable<typeof e> => e !== null) as EpisodeOfCare[];
        const patientIds = [
          ...new Set(
            episodes
              .map((e) => e.patient?.reference?.replace('Patient/', ''))
              .filter((id): id is string => Boolean(id)),
          ),
        ];
        const patientResults =
          patientIds.length > 0
            ? await Promise.all(
              patientIds.map((id) => medplum.readResource('Patient', id).catch(() => null)),
            )
            : [];
        const patients = patientResults.filter((p): p is NonNullable<typeof p> => p !== null) as Patient[];
        const patientById: Record<string, Patient> = {};
        for (const p of patients as Patient[]) {
          if (p.id) patientById[p.id] = p;
        }
        const infoMap: Record<string, EpisodeInfo> = {};
        for (const episode of episodes as EpisodeOfCare[]) {
          if (!episode.id) continue;
          const patientId = episode.patient?.reference?.replace('Patient/', '') ?? '';
          const patient = patientById[patientId];
          const patientName = patient?.name?.[0] ? formatHumanName(patient.name[0]) : '';
          const serviceType = displayLabelForEpisodeType(episode.type?.[0]);
          infoMap[episode.id] = { patientName, serviceType, patientId };
        }
        setEpisodeInfoMap(infoMap);
      } catch (e) {
        console.error('Failed to resolve episode info', e);
      } finally {
        setEpisodesLoading(false);
      }
    })();
  }, [viewedRequest, medplum]);

  const filteredRequests = useMemo(() => {
    let filtered = [...rows];

    if (statusFilter.length === 0) {
      filtered = [];
    } else {
      filtered = filtered.filter((r) => statusFilter.includes(r.status));
    }

    return filtered;
  }, [rows, statusFilter]);

  const columns = useMemo(
    (): UnifyTableColumn<ReferralTaskRow>[] => [
      {
        key: 'requestDate',
        label: 'Request Date',
        sortable: true,
        getValue: (row) => row.requestDate,
        render: (row) => <Text size="sm">{formatUsDateTime(row.requestDate) || '—'}</Text>,
      },
      {
        key: 'fill',
        label: 'Fill Fraction',
        sortable: false,
        getValue: (row) => String(row.referralsPending + row.referralsConverted),
        render: (row) => {
          const total = row.numberOfClients;
          const committed = Math.min(row.referralsPending + row.referralsConverted, total);
          const reserved = Math.min(row.referralsReserved, Math.max(0, total - committed));
          const committedPct = getFillPercentage(committed, total);
          const inProgressPct = getFillPercentage(reserved, total);
          return (
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                {committed} / {total} filled
                {reserved > 0 && ` · ${reserved} in progress`}
              </Text>
              <Progress.Root size="sm">
                <Progress.Section value={committedPct} color={committed >= total ? 'green' : 'blue'} />
                {reserved > 0 && <Progress.Section value={inProgressPct} color="yellow" />}
              </Progress.Root>
            </Stack>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        sortable: true,
        getValue: (row) => row.status,
        render: (row) => (
          <Badge color={getStatusColor(row.status)} variant="light">
            {row.status}
          </Badge>
        ),
      },
      {
        key: 'lastUpdateDate',
        label: 'Last Assigned',
        sortable: true,
        getValue: (row) => row.lastUpdateDate,
        render: (row) => <Text size="sm">{formatUsDateTime(row.lastUpdateDate) || '—'}</Text>,
      },
    ],
    [],
  );

  const handleOpenRequestModal = () => {
    setRequestModalOpen(true);
  };

  const handleCancelRequest = () => {
    if (!viewedRequest) return;
    void (async () => {
      try {
        await cancelReferralRequest(viewedRequest.sourceTask);
        notifications.show({
          title: viewedRequest.referralsReserved > 0 ? 'Cancellation queued' : 'Request cancelled',
          message:
            viewedRequest.referralsReserved > 0
              ? 'Cancellation will complete when the in-progress intake reservation clears.'
              : 'The referral request was cancelled.',
          color: 'teal',
        });
        setViewedRequest(null);
      } catch (e) {
        console.error(e);
        notifications.show({
          title: 'Could not cancel',
          message: e instanceof Error ? e.message : 'Unknown error',
          color: 'red',
        });
      }
    })();
  };

  const detailTask: Task | null = viewedRequest?.sourceTask ?? null;
  const prefs = detailTask ? taskPreferencesFromRow(detailTask) : null;
  const assignmentOutputs =
    detailTask?.output?.filter((o) => isAssignedEpisodeOutput(o)) ?? [];

  return (
    <>
      <CaseloadTabPanelLayout
        filters={
          <>
            {showSuperviseeProviderFilter ? (
              <FilterMultiSelect
                allLabel="All Referrals"
                data={providerFilterOptions}
                value={providerFilter}
                onChange={(next) => {
                  setProviderFilter((previous) => normalizeProviderFilter(next, previous));
                }}
                emptyMeansAll={false}
                optionsAboveDivider={[CURRENT_PROVIDER_ID]}
                groupLabelBelow="Team"
              />
            ) : null}
            <FilterMultiSelect
              allLabel="All Statuses"
              data={[
                { value: 'Pending', label: 'Pending' },
                { value: 'In Progress', label: 'In Progress' },
                { value: 'Completed', label: 'Completed' },
                { value: 'Cancelled', label: 'Cancelled' },
              ]}
              value={statusFilter}
              onChange={(next) => {
                setStatusFilter(next);
              }}
            />
          </>
        }
        filterActions={
          <Button leftSection={<IconPlus size={16} />} onClick={handleOpenRequestModal}>
            Request Referrals
          </Button>
        }
        alerts={
          error ? (
            <Alert color="red" title="Could not load referrals">
              {error}{' '}
              <Button variant="light" size="xs" onClick={() => void refetch()}>
                Retry
              </Button>
            </Alert>
          ) : undefined
        }
      >
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <UnifyTable<ReferralTaskRow>
            withPaper={false}
            searchable={false}
            columns={columns}
            data={filteredRequests}
            getRowKey={(row) => row.id}
            sortable
            pagination
            defaultRowsPerPage={50}
            emptyMessage="No referral requests found matching your criteria"
            onRowClick={(row) => setViewedRequest(row)}
            tableProps={{ highlightOnHover: true }}
          />
        )}
      </CaseloadTabPanelLayout>

      <ReferralRequestCreateModal
        opened={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        lockedPractitionerId={lockedPractitionerId}
        providersById={providersById}
        onSubmit={async (values) => {
          try {
            await createReferralRequest({
              numberOfClients: values.numberOfClients,
              acceptsInPerson: values.acceptsInPerson,
              noMinors: values.noMinors,
              noCouplesFamilies: values.noCouplesFamilies,
              onlyCashPay: values.onlyCashPay,
            });
            notifications.show({
              title: 'Referral request created',
              message: 'Your capacity request was saved.',
              color: 'green',
            });
            setRequestModalOpen(false);
          } catch (e) {
            console.error(e);
            notifications.show({
              title: 'Could not create request',
              message: e instanceof Error ? e.message : 'Unknown error',
              color: 'red',
            });
          }
        }}
      />

      <Modal opened={viewedRequest !== null} onClose={() => setViewedRequest(null)} title="Request details" size="lg" centered>
        {viewedRequest && detailTask && prefs && (
          <Stack gap="lg">
            {viewedRequest.cancellationQueued && (
              <Alert color="yellow" title="Cancellation pending">
                A client is completing intake on a reserved slot. This request will cancel automatically when the
                reservation clears.
              </Alert>
            )}
            <Group grow>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Requested
                </Text>
                <Text size="sm" fw={500}>
                  {formatUsDateTime(viewedRequest.requestDate) || '—'}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Slots requested
                </Text>
                <Text size="sm" fw={500}>
                  {viewedRequest.numberOfClients}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                  Status
                </Text>
                <Badge color={getStatusColor(viewedRequest.status)} variant="light" size="sm">
                  {viewedRequest.status}
                </Badge>
              </div>
            </Group>

            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Preferences
              </Text>
              <Group gap="xs">
                {prefs.acceptsInPerson && (
                  <Badge variant="light" color="gray" size="sm">
                    In-person
                  </Badge>
                )}
                {prefs.noMinors && (
                  <Badge variant="light" color="gray" size="sm">
                    No minors
                  </Badge>
                )}
                {prefs.noCouplesFamilies && (
                  <Badge variant="light" color="gray" size="sm">
                    No couples/families
                  </Badge>
                )}
                {prefs.onlyCashPay && (
                  <Badge variant="light" color="gray" size="sm">
                    Cash-pay only
                  </Badge>
                )}
              </Group>
            </Stack>

            <div>
              <Text size="sm" fw={600} mb="xs">
                Assignments
              </Text>
              {assignmentOutputs.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No episode assignments yet.
                </Text>
              ) : (
                <Table highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Episode</Table.Th>
                      <Table.Th>Disposition</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {assignmentOutputs.map((o, idx) => {
                      const d = getOutputAssignmentDisposition(o) ?? 'pending';
                      const label =
                        d === 'converted' ? 'Converted' : d === 'declined' ? 'Declined' : d === 'reserved' ? 'Reserved' : 'Pending';
                      const color =
                        d === 'converted' ? 'green' : d === 'declined' ? 'red' : d === 'reserved' ? 'yellow' : 'gray';
                      const rawRef = o.valueReference?.reference ?? '';
                      const episodeId = rawRef.replace('EpisodeOfCare/', '');
                      const info = episodeInfoMap[episodeId];
                      const displayLabel = info
                        ? [info.patientName, info.serviceType].filter(Boolean).join(' – ')
                        : null;
                      return (
                        <Table.Tr key={`${viewedRequest.id}-out-${idx}`}>
                          <Table.Td>
                            {info ? (
                              <Anchor
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/dashboard/clients/${info.patientId}/episode-of-care/${episodeId}`,
                                  );
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                {displayLabel}
                              </Anchor>
                            ) : (
                              <Text size="sm" c={episodesLoading ? 'dimmed' : undefined}>
                                {rawRef || '—'}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Badge variant="light" color={color} size="sm">
                              {label}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </div>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setViewedRequest(null)}>
                Close
              </Button>
              <Button
                color="red"
                onClick={handleCancelRequest}
                disabled={viewedRequest.status === 'Cancelled' || viewedRequest.status === 'Completed'}
              >
                Cancel Request
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}
